import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Copy, ExternalLink } from 'lucide-react';

import { getPublicSurveyDashboard } from '../../services/surveyService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px 24px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 2px 8px rgba(0, 66, 66, 0.12); }
`;

const CardLeft = styled.div`
  flex: 1;
`;

const CardTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardMeta = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'active' ? '#16a34a15' :
    $status === 'closed' ? '#2563eb15' : '#64748b15'};
  color: ${({ $status }) =>
    $status === 'active' ? '#16a34a' :
    $status === 'closed' ? '#2563eb' : '#64748b'};
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: #64748b;
  border-radius: 4px;
  display: flex;
  &:hover { background: #f1f5f9; color: ${({ theme }) => theme.colors.primary.main}; }
`;

const EmptyText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  text-align: center;
  padding: 60px 0;
`;

export default function PublicSurveyDashboardPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicSurveyDashboard({ pageNumber: 1, pageSize: 50 })
      .then(res => setSurveys(res.data?.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyLink = useCallback((e, token) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/s/${token}`);
  }, []);

  const statusLabels = {
    active: t('survey.active'),
    closed: t('survey.closed'),
    draft: t('survey.draft'),
  };

  const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();

  if (loading) return <Container><EmptyText>...</EmptyText></Container>;

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('menu.publicSurveyDashboard') }]} />
      <Content>
        {surveys.length === 0 ? (
          <EmptyText>{t('common.noRecords')}</EmptyText>
        ) : (
          surveys.map(s => (
            <Card key={s.id} onClick={() => history.push(`/surveys/${s.id}`)}>
              <CardLeft>
                <CardTitle>{s.title}</CardTitle>
                <CardMeta>
                  <StatusBadge $status={s.status}>{statusLabels[s.status] || s.status}</StatusBadge>
                  <span>{t('survey.responseCount')}: {s.publicResponseCount}{s.maxResponses > 0 ? ` / ${s.maxResponses}` : ''}</span>
                  {s.expiresAt && (
                    <span style={isExpired(s.expiresAt) ? { color: '#E53935' } : {}}>
                      {t('survey.expiresAt')}: {new Date(s.expiresAt).toLocaleDateString('tr-TR')}
                      {isExpired(s.expiresAt) && ` (${t('survey.expired')})`}
                    </span>
                  )}
                </CardMeta>
              </CardLeft>
              <CardActions>
                <IconButton onClick={(e) => copyLink(e, s.publicToken)} title={t('survey.copyLink')}>
                  <Copy size={16} />
                </IconButton>
                <IconButton onClick={(e) => { e.stopPropagation(); window.open(`/s/${s.publicToken}`, '_blank'); }} title={t('survey.publicLink')}>
                  <ExternalLink size={16} />
                </IconButton>
              </CardActions>
            </Card>
          ))
        )}
      </Content>
    </Container>
  );
}
